// ─────────────────────────────────────────────────────────────
// LES CONFRÉRIES
//
// Les habitants ne s'achètent pas et ne sortent pas des coffres.
// Chacun est la récompense d'une CONFRÉRIE : un lot de 3 bâtiments
// qui partagent un thème. Réunis les 3, l'habitant emménage — et
// devient ensuite rachetable au comptoir (voir economy.js, NPC_PRICE).
//
// C'est le seul fichier à toucher pour ajouter un thème complet à la
// main. L'admin peut aussi en créer depuis l'interface : elles sont
// injectées à l'exécution par setCustomSets() plus bas.
//
// IMPORTANT — mutabilité : SETS, SET_BY_ID et SET_OF_BUILDING sont
// modifiés EN PLACE, jamais réassignés, pour que tous les modules qui
// les ont importés voient les ajouts sans recharger la page.
// ─────────────────────────────────────────────────────────────

import { t } from "@/i18n";

// b()  → bâtiment d'une case
// bN() → grille n×n de blocs identiques
// bC() → bâtiment composé de plusieurs tuiles (emprise libre)
const b = (id, nameKey, descKey, rarity, tile) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  rarity,
  tile,
  size: [1, 1],
});
const bN = (id, nameKey, descKey, rarity, tile, n) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  rarity,
  tile,
  size: [n, n],
  parts: Array.from({ length: n * n }, (_, i) => ({
    tile,
    dx: i % n,
    dy: Math.floor(i / n),
  })),
});
const bC = (id, nameKey, descKey, rarity, size, parts) => ({
  id,
  get name() {
    return t(nameKey);
  },
  get desc() {
    return t(descKey);
  },
  nameKey,
  descKey,
  rarity,
  tile: parts[0].tile,
  size,
  parts,
});

const BASE_SETS = [
  {
    id: "campement",
    get name() {
      return t("sets.campement_name");
    },
    get tagline() {
      return t("sets.campement_tagline");
    },
    color: "#c98a4b",
    npc: {
      id: "caravanier",
      get name() {
        return t("sets.caravanier_name");
      },
      get desc() {
        return t("sets.caravanier_desc");
      },
      look: "caravanier",
    },
    buildings: [
      b(
        "camp_khaima",
        "sets.camp_khaima_name",
        "sets.camp_khaima_desc",
        "commun",
        "tent",
      ),
      b(
        "camp_tente_basse",
        "sets.camp_tente_basse_name",
        "sets.camp_tente_basse_desc",
        "commun",
        "tentSlant",
      ),
      bC(
        "camp_enclos",
        "sets.camp_enclos_name",
        "sets.camp_enclos_desc",
        "peu_commun",
        [3, 1],
        [
          { tile: "wallCorner", dx: 0, dy: 0 },
          { tile: "wallSides", dx: 1, dy: 0 },
          { tile: "wallEnd", dx: 2, dy: 0 },
        ],
      ),
    ],
  },
  {
    id: "souk",
    get name() {
      return t("sets.souk_name");
    },
    get tagline() {
      return t("sets.souk_tagline");
    },
    color: "#e0873f",
    npc: {
      id: "marchand",
      get name() {
        return t("sets.marchand_name");
      },
      get desc() {
        return t("sets.marchand_desc");
      },
      look: "marchand",
    },
    buildings: [
      b(
        "souk_etal",
        "sets.souk_etal_name",
        "sets.souk_etal_desc",
        "commun",
        "overhang",
      ),
      b(
        "souk_entrepot",
        "sets.souk_entrepot_name",
        "sets.souk_entrepot_desc",
        "peu_commun",
        "house",
      ),
      bC(
        "souk_halle",
        "sets.souk_halle_name",
        "sets.souk_halle_desc",
        "rare",
        [2, 2],
        [
          { tile: "floorDecorated", dx: 0, dy: 0 },
          { tile: "floorDecorated", dx: 1, dy: 0 },
          { tile: "tent", dx: 0, dy: 1 },
          { tile: "tentSlant", dx: 1, dy: 1 },
        ],
      ),
    ],
  },
  {
    id: "tisserands",
    get name() {
      return t("sets.tisserands_name");
    },
    get tagline() {
      return t("sets.tisserands_tagline");
    },
    color: "#2f9e9e",
    npc: {
      id: "tisseuse",
      get name() {
        return t("sets.tisseuse_name");
      },
      get desc() {
        return t("sets.tisseuse_desc");
      },
      look: "tisseuse",
    },
    buildings: [
      b(
        "tiss_atelier",
        "sets.tiss_atelier_name",
        "sets.tiss_atelier_desc",
        "commun",
        "houseWindows",
      ),
      b(
        "tiss_sechoir",
        "sets.tiss_sechoir_name",
        "sets.tiss_sechoir_desc",
        "peu_commun",
        "overhangSmall",
      ),
      bN(
        "tiss_maison",
        "sets.tiss_maison_name",
        "sets.tiss_maison_desc",
        "rare",
        "houseDoor",
        2,
      ),
    ],
  },
  {
    id: "halqa",
    get name() {
      return t("sets.halqa_name");
    },
    get tagline() {
      return t("sets.halqa_tagline");
    },
    color: "#8b5fa8",
    npc: {
      id: "conteur",
      get name() {
        return t("sets.conteur_name");
      },
      get desc() {
        return t("sets.conteur_desc");
      },
      look: "conteur",
    },
    buildings: [
      b(
        "halqa_estrade",
        "sets.halqa_estrade_name",
        "sets.halqa_estrade_desc",
        "peu_commun",
        "floorTiles",
      ),
      bC(
        "halqa_cour",
        "sets.halqa_cour_name",
        "sets.halqa_cour_desc",
        "peu_commun",
        [2, 2],
        [
          { tile: "floorDecorated", dx: 0, dy: 0 },
          { tile: "floorCrumbled", dx: 1, dy: 0 },
          { tile: "floorDecorated", dx: 0, dy: 1 },
          { tile: "stairsFull", dx: 1, dy: 1 },
        ],
      ),
      b(
        "halqa_dome",
        "sets.halqa_dome_name",
        "sets.halqa_dome_desc",
        "rare",
        "dome",
      ),
    ],
  },
  {
    id: "musique",
    get name() {
      return t("sets.musique_name");
    },
    get tagline() {
      return t("sets.musique_tagline");
    },
    color: "#4f7fc4",
    npc: {
      id: "musicien",
      get name() {
        return t("sets.musicien_name");
      },
      get desc() {
        return t("sets.musicien_desc");
      },
      look: "musicien",
    },
    buildings: [
      b(
        "mus_kiosque",
        "sets.mus_kiosque_name",
        "sets.mus_kiosque_desc",
        "commun",
        "domeSmall",
      ),
      b(
        "mus_auvent",
        "sets.mus_auvent_name",
        "sets.mus_auvent_desc",
        "peu_commun",
        "overhang",
      ),
      bC(
        "mus_scene",
        "sets.mus_scene_name",
        "sets.mus_scene_desc",
        "epique",
        [3, 1],
        [
          { tile: "floorSteps", dx: 0, dy: 0 },
          { tile: "floorDecorated", dx: 1, dy: 0 },
          { tile: "floorSteps", dx: 2, dy: 0 },
        ],
      ),
    ],
  },
  {
    id: "eaux",
    get name() {
      return t("sets.eaux_name");
    },
    get tagline() {
      return t("sets.eaux_tagline");
    },
    color: "#3fa5d9",
    npc: {
      id: "guerrab",
      get name() {
        return t("sets.guerrab_name");
      },
      get desc() {
        return t("sets.guerrab_desc");
      },
      look: "guerrab",
    },
    buildings: [
      b(
        "eaux_citerne",
        "sets.eaux_citerne_name",
        "sets.eaux_citerne_desc",
        "commun",
        "waterCenter",
      ),
      b(
        "eaux_noria",
        "sets.eaux_noria_name",
        "sets.eaux_noria_desc",
        "rare",
        "waterFall",
      ),
      bC(
        "eaux_bassin",
        "sets.eaux_bassin_name",
        "sets.eaux_bassin_desc",
        "epique",
        [2, 2],
        [
          { tile: "waterCenter", dx: 0, dy: 0 },
          { tile: "waterCenter", dx: 1, dy: 0 },
          { tile: "waterFall", dx: 0, dy: 1 },
          { tile: "floorTiles", dx: 1, dy: 1 },
        ],
      ),
    ],
  },
  {
    id: "msid",
    get name() {
      return t("sets.msid_name");
    },
    get tagline() {
      return t("sets.msid_tagline");
    },
    color: "#c9a227",
    npc: {
      id: "enfant",
      get name() {
        return t("sets.msid_name");
      }, // Using appropriate fallbacks/keys
      get desc() {
        return t("sets.enfant_desc");
      },
      look: "enfant",
    },
    buildings: [
      b(
        "msid_salle",
        "sets.msid_salle_name",
        "sets.msid_salle_desc",
        "peu_commun",
        "houseSide",
      ),
      bC(
        "msid_cour",
        "sets.msid_cour_name",
        "sets.msid_cour_desc",
        "rare",
        [2, 2],
        [
          { tile: "floorTiles", dx: 0, dy: 0 },
          { tile: "floorTiles", dx: 1, dy: 0 },
          { tile: "wallSquare", dx: 0, dy: 1 },
          { tile: "stairsLeft", dx: 1, dy: 1 },
        ],
      ),
      bC(
        "msid_medersa",
        "sets.msid_medersa_name",
        "sets.msid_medersa_desc",
        "legendaire",
        [2, 3],
        [
          { tile: "houseDarkSide", dx: 0, dy: 0 },
          { tile: "houseWindows", dx: 1, dy: 0 },
          { tile: "floorTiles", dx: 0, dy: 1 },
          { tile: "floorDecorated", dx: 1, dy: 1 },
          { tile: "wallSquare", dx: 0, dy: 2 },
          { tile: "dome", dx: 1, dy: 2 },
        ],
      ),
    ],
  },
];

export const SETS = [...BASE_SETS];
const BASE_SET_IDS = new Set(BASE_SETS.map((s) => s.id));

export const SET_BY_ID = {};
export const SET_OF_BUILDING = {};

function reindex() {
  for (const k of Object.keys(SET_BY_ID)) delete SET_BY_ID[k];
  for (const k of Object.keys(SET_OF_BUILDING)) delete SET_OF_BUILDING[k];
  for (const set of SETS) {
    SET_BY_ID[set.id] = set;
    for (const bld of set.buildings) SET_OF_BUILDING[bld.id] = set.id;
  }
}
reindex();

export function setCustomSets(defs = [], items = []) {
  for (let i = SETS.length - 1; i >= 0; i--) {
    if (!BASE_SET_IDS.has(SETS[i].id)) SETS.splice(i, 1);
  }
  for (const d of defs) {
    if (!d?.id || BASE_SET_IDS.has(d.id)) continue;
    const buildings = items
      .filter((it) => it.set === d.id)
      .map((it) => ({
        id: it.id,
        name: it.name || it.id,
        desc: it.desc || "",
        rarity: it.rarity || "commun",
        tile: it.tile || null,
        size: it.size || [1, 1],
      }));
    SETS.push({
      id: d.id,
      name: d.name || d.id,
      tagline: d.tagline || "",
      color: d.color || "#c98a4b",
      npc: {
        id: d.npc?.id || `${d.id}_habitant`,
        name: d.npc?.name || "Habitant",
        desc: d.npc?.desc || "",
        look: d.npc?.look || "marchand",
      },
      buildings,
      custom: true,
    });
  }
  reindex();
}

export function setProgress(set, inventory) {
  const owned = set.buildings.filter((bld) => (inventory[bld.id] || 0) > 0);
  return {
    owned: owned.length,
    total: set.buildings.length,
    complete: set.buildings.length > 0 && owned.length === set.buildings.length,
  };
}

export function pendingRewards(inventory) {
  return SETS.filter(
    (s) => setProgress(s, inventory).complete && !(inventory[s.npc.id] > 0),
  );
}
